import { useEffect, useRef } from 'react';
import { appearanceCatalog, isPcSheetRef, type AvatarAppearance, type PlayerAppearance } from '@campus/core';
import { DEFAULT_APPEARANCE } from '../../game/sprites/CompositeAvatar.js';
import { API_BASE } from '../../lib/apiBase.js';

interface CatalogEntry {
  id: string;
  url: string;
  layer: string;
  recolorable: boolean;
  frameWidth: number;
  frameHeight: number;
  animations: Record<string, { start: number }>;
}

const catalog = appearanceCatalog as unknown as CatalogEntry[];
const FRAME_W = 16;
const FRAME_H = 32;
const imageCache = new Map<string, Promise<HTMLImageElement>>();

function loadImage(url: string): Promise<HTMLImageElement> {
  let p = imageCache.get(url);
  if (!p) {
    p = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
    imageCache.set(url, p);
  }
  return p;
}

type ThumbProps = {
  appearance: AvatarAppearance | null;
  size?: number;
  /** `head` : tête et haut du buste (pastilles). `full` : avatar en entier, `size` = hauteur. */
  framing?: 'head' | 'full';
  /** Sans fond ni découpe ronde (vignettes du choix d'avatar, grand aperçu). */
  bare?: boolean;
  className?: string;
};

/** Aperçu de l'avatar (image fixe de face) — planche Pixel Citizen ou calques LimeZu. */
export function AvatarThumb(props: ThumbProps) {
  const { appearance } = props;
  if (isPcSheetRef(appearance)) return <PcThumb {...props} sheet={appearance.sheet} />;
  return <LegacyThumb {...props} appearance={appearance} />;
}

/**
 * Avatar Pixel Citizen : première image de la planche (repos, de face), cadrée
 * en CSS — pas de canvas, la planche est déjà assemblée par l'API.
 * Cases 48×48 ; tête d'environ x 12-36, y 5-29 ; personnage entier y 4-46.
 */
function PcThumb({ sheet, size = 28, framing = 'head', bare = false, className }: ThumbProps & { sheet: string }) {
  const full = framing === 'full';
  const box = full ? { x: 12, y: 4, w: 24, h: 42 } : { x: 12, y: 5, w: 24, h: 24 };
  const scale = size / box.h;
  return (
    <span
      className={className}
      aria-hidden="true"
      style={{
        display: 'inline-block', width: box.w * scale, height: size, flexShrink: 0,
        borderRadius: bare ? 0 : 'var(--radius-full)',
        backgroundColor: bare ? 'transparent' : 'var(--color-bg-overlay)',
        backgroundImage: `url(${API_BASE}/assets/shared/avatars/pc/${sheet}.png)`,
        backgroundRepeat: 'no-repeat',
        // Hauteur fixe (4 directions) ; largeur libre : 16 colonnes (Pixel Citizen) ou 20 (Campus, poses assises).
        backgroundSize: `auto ${192 * scale}px`,
        backgroundPosition: `${-box.x * scale}px ${-box.y * scale}px`,
        imageRendering: 'pixelated',
      }}
    />
  );
}

/**
 * Aperçu de l'avatar (image fixe de face), recomposé à partir des mêmes calques
 * que CompositeAvatar : corps, yeux, tenue, cheveux (teintés comme Phaser setTint,
 * par multiplication), accessoire. Cadré sur la tête et le haut du buste.
 */
function LegacyThumb({ appearance, size = 28, framing = 'head', bare = false, className }: ThumbProps & {
  appearance: PlayerAppearance | null;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const a = appearance ?? DEFAULT_APPEARANCE;

  useEffect(() => {
    let cancelled = false;
    const ids = [a.bodyId, a.eyesId, a.outfitId, a.hairId, a.accessoryId].filter((id): id is string => !!id);
    const entries = ids
      .map((id) => catalog.find((e) => e.id === id))
      .filter((e): e is CatalogEntry => !!e);

    void Promise.all(entries.map((e) => loadImage(e.url).then((img) => ({ e, img })))).then((layers) => {
      const canvas = canvasRef.current;
      if (cancelled || !canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, FRAME_W, FRAME_H);
      const scratch = document.createElement('canvas');
      scratch.width = FRAME_W;
      scratch.height = FRAME_H;
      const sctx = scratch.getContext('2d');
      if (!sctx) return;
      for (const { e, img } of layers) {
        const frame = e.animations['idle_down']?.start ?? 3;
        const cols = Math.max(1, Math.floor(img.width / FRAME_W));
        sctx.clearRect(0, 0, FRAME_W, FRAME_H);
        sctx.drawImage(img, (frame % cols) * FRAME_W, Math.floor(frame / cols) * FRAME_H, FRAME_W, FRAME_H, 0, 0, FRAME_W, FRAME_H);
        if (e.layer === 'hair' && e.recolorable && a.hairColor != null) {
          const r = (a.hairColor >> 16) & 255, g = (a.hairColor >> 8) & 255, b = a.hairColor & 255;
          const d = sctx.getImageData(0, 0, FRAME_W, FRAME_H);
          for (let i = 0; i < d.data.length; i += 4) {
            d.data[i] = (d.data[i]! * r) / 255;
            d.data[i + 1] = (d.data[i + 1]! * g) / 255;
            d.data[i + 2] = (d.data[i + 2]! * b) / 255;
          }
          sctx.putImageData(d, 0, 0);
        }
        ctx.drawImage(scratch, 0, 0);
      }
    }).catch(() => { /* aperçu vide plutôt qu'une erreur : l'avatar reste visible en jeu */ });

    return () => { cancelled = true; };
  }, [a.bodyId, a.eyesId, a.outfitId, a.hairId, a.accessoryId, a.hairColor]);

  // La tête occupe environ les rangées 10 à 26 de l'image 16×32 : on cadre dessus.
  const full = framing === 'full';
  const scale = full ? size / FRAME_H : size / FRAME_W;
  return (
    <span
      className={className}
      aria-hidden="true"
      style={{
        position: 'relative', display: 'inline-block', width: full ? FRAME_W * scale : size, height: size, flexShrink: 0,
        borderRadius: bare ? 0 : 'var(--radius-full)', overflow: 'hidden', background: bare ? 'transparent' : 'var(--color-bg-overlay)',
      }}
    >
      <canvas
        ref={canvasRef}
        width={FRAME_W}
        height={FRAME_H}
        style={{
          position: 'absolute', left: 0, top: full ? 0 : -Math.round(10 * scale),
          width: FRAME_W * scale, height: FRAME_H * scale, imageRendering: 'pixelated',
        }}
      />
    </span>
  );
}
